import DataTable, { TableOptions, TableVisibility } from '@/components/data-table/DataTable';
import { Product, ProductImage } from '@/models/Product';
import { Component, Prop, Ref, Vue } from 'vue-property-decorator';
import { Color } from '@/models/Color';
import { Size } from '@/models/Size';
import { Brand } from '@/models/Brand';
import { Supplier } from '@/models/Supplier';
import { formatDate } from '@/support/String';
import { Category } from '@/models/Category';
import { ProductVariant, ProductVariantIncludes } from '@/models/ProductVariant';
import ProductAttributesDialog from '@/views/Variants/ProductAttributesDialog';
import { ActionItem } from '@/components/ActionList/ActionList';
import { Options } from '@/components/mi-dialog/MiDialog';
import { Order } from '@/models/Order';
import ErrorHandler from '@/support/ErrorHandler';
import BulkUpdateProducts from '@/views/Variants/BulkUpdateProducts';
import UploadProductCsvDialog from '@/views/Variants/UploadProductCsvDialog';
import UploadProductImageDialog from '@/views/Variants/UploadProductImageDialog';
import { axiosDownload } from '@/support/Client';
import { Season } from '@/models/Season';
import { thumbnailTransformer } from '@/support/ThumbnailTransformer';

@Component<ProductVariantDatatable>({
  components: {
    ProductAttributesDialog,
    BulkUpdateProducts,
    UploadProductCsvDialog,
    UploadProductImageDialog
  }
})
export default class ProductVariantDatatable extends Vue {
  //#region @Props
  /**
  * ie.
  * @Prop()
  * protected user!: User
  */

  @Prop()
  protected product!: Product;

  @Prop()
  protected visibility!: TableVisibility;
  //#endregion


  //#region @Refs
  /**
  * ie.
  * @Ref()
  * readonly anotherComponent!: AnotherComponent
  */

  @Ref()
  readonly productVariantDatatable!: DataTable;
  //#endregion


  //#region Class properties
  /**
  * ie.
  * protected isLoading = true;
  * * protected company: Company | null = null;
  */

  protected isExportingProductVariants = false;
  protected isUploadingImages = false;
  protected isUploadingCsv = false;
  protected isUpdatingBulk = false;
  protected isDisplayingQuickAddProductsDialog = false;
  protected isDisplayingAttributes = false;
  protected variant: ProductVariant | null = null;
  protected selectedProductVariants: ProductVariant[] = [];
  protected selectedOrder: Order | null = null;

  protected isLoading = true;
  protected interalVisibility: TableVisibility = {
    search: true,
    total: false,
    limit: true,
    pagination: true,
    checkboxes: true,
    title: false,
  };
  //#endregion


  //#region Lifecycle Hooks / Init
  /**
  * ie.
  * protected mounted(): void {
  *  console.log('MyComponent has Mounted');
  * }
  */

  protected mounted(): void {
    this.interalVisibility = {...this.interalVisibility, ...this.visibility};
    this.isLoading = false;
  }
  //#endregion


  //#region Class methods
  /**
  * ie.
  * protected toggleLoading(): void {
  *  this.isLoading = ! this.isLoading;
  * }
  */

  protected openCloneVariantDialog(variant: ProductVariant) {
    this.variant = variant;
    this.isDisplayingQuickAddProductsDialog = true;
  }

  protected openIsDisplayAttributes(variant: ProductVariant) {
    this.variant = variant;
    this.isDisplayingAttributes = true;
  }

  public refreshProductVariantDatatable() {
    this.productVariantDatatable ? this.productVariantDatatable.refresh() : null;
  }
  //#endregion


  //#region Async methods
  /**
  * ie.
  * protected async fetchUserCompany(): Promise<void> {
  *  this.company = await new Company().filter({user: this.user.id}).all();
  * }
   */

  protected async returnProducts(): Promise<void> {
    try {
      const order = await new ProductVariant()
      .returnToSupplier({
        product_variants: this.selectedProductVariants
          ? this.selectedProductVariants.map((product: any) => {
              return product.id;
            })
          : []
      })
      this.selectedOrder = order;
      this.$store.dispatch("openDialog", this.dialogReturingProductsSuccess);
    } catch (error) {
      ErrorHandler.network(error);
    }

  }

  protected async exportProductVariants(): Promise<void> {
    this.isExportingProductVariants = true;
    let payload: exportPayload = {};

    if (this.selectedProductVariants.length) {
      payload.product_variants = (this.selectedProductVariants.map((product: ProductVariant) => product.id) as string[]);
    };

    try {
      await axiosDownload({
        url: new Product().exportProduct(this.product.id),
        accept: 'text/csv; charset=utf-8'
      }, payload);
    } catch (error) {
      ErrorHandler.network(error);
    }
    this.isExportingProductVariants = false;
  }
  //#endregion


  //#region Getters & Setters
  /**
   * ie.
   * protected get companyName(): string {
   *  return this?.company.name || 'N/A';
   * }
   */

  protected get tableOptions(): TableOptions {
    return {
      model: new ProductVariant()
      .filter({product: this.product.id})
      .include([
        ProductVariantIncludes.SEASON,
        ProductVariantIncludes.IMAGES,
        ProductVariantIncludes.TAGS,
        ProductVariantIncludes.AVAILABLE_SIZES,
        ProductVariantIncludes.CATEGORIES,
        ProductVariantIncludes.ATTRIBUTES_COUNT,
        ProductVariantIncludes.COLOR,
        ProductVariantIncludes.BRAND,
        ProductVariantIncludes.SUPPLIER,
        ProductVariantIncludes.SIZE,
      ]),
      sort: {
          key: 'created_at',
          order: 'DESC'
        },
        headers: [
          {
            text: '',
            value: 'image_url',
            width: '1%',
            transform: (image: ProductImage) => image && image.url_large ? thumbnailTransformer(image.url_large) : '',
          },
          {
            text: `<span title="Product Name / EAN / SKU / Supplier Style name">Product Name</span>`,
            value: 'product_name',
            action: '/varianten/{id}',
            sortable: {
              key: 'product_name',
              order: 'DESC'
            },
            transform: (name: string, variant: ProductVariant) => {
              return `<div class='custom-name'>
                      ${ this.product.name ? `<span><strong>${this.product.name}</strong></span>` : ''}
                      ${ variant.upc ? `<span>${variant.upc}</span>` : ''}
                      ${ variant.sku ? `<span class='text-grey'>${variant.sku}</span>` : ''}
                      ${ variant.supplier_style_name ? `<span class='text-light-grey'>${variant.supplier_style_name}</span>` : ''}
                      </div>
                      `;
            }
          },
          {
            text: 'Product ID',
            value: 'product_id',
          },
          {
            text: 'Supplier / Brand',
            value: 'brand',
            transform: (value: Brand, productVariant: ProductVariant) => {
              const brand = (productVariant.brand as Brand);
              const supplier = (productVariant.supplier as Supplier);
              return `${brand && brand.name ? brand.name : ''} <br> ${supplier && supplier.name ? supplier.name : ''}`;
            }
          },
          {
            text: 'Category',
            value: 'category',
            sortable: {
              key: 'category',
              order: 'DESC'
            },
            transform: (name: string, product: ProductVariant) => {
              if (product.hasOwnProperty('categories')) {
                return (product.categories as Category[])
                  .map(categoryObj => categoryObj.name)
                  .sort()
                  .join(', ');
              } else {
                return '';
              }
            }
          },
          {
            text: 'Status',
            value: 'status',
            transform: (status: string) => {
              return `<span class='statusPill statusPill--${status}'>${status}</span>`;
            }
          },
          {
            text: 'Color / Size',
            value: 'brand',
            transform: (value: Brand, productVariant: ProductVariant) => {
              const color = (productVariant.color as Color);
              const size = (productVariant.size as Size);
              return `${color && color.name ? color.name : ''} <br> ${size && size.name ? size.name : ''}`;
            }
          },
          {
            text: 'Supplier Color / Size',
            value: 'brand',
            transform: (value: Brand, productVariant: ProductVariant) => {
              return `${productVariant.supplier_color} <br> ${productVariant.supplier_size}`;
            }
          },
          {
            text: 'Season',
            value: 'season',
            transform: (season: Season) => {
              return season && season.name ? season.name : '';
            }
          },
          {
            text: 'Price',
            value: 'created_at',
            transform: (date: string, productVariant: ProductVariant) => {
              return `<div class="text-right">
                      <span class="d-block white-space-no-wrap">R: € ${productVariant.retail_price}</span>
                      <span class="d-block white-space-no-wrap">B: € ${productVariant.buy_price}</span>
                      </div>
                      `;
            },
          },
          {
            text: 'Attr',
            value: 'attributes_count',
            action: (productVariant: ProductVariant) => {
              if (!productVariant || !productVariant.attributes_count) {
                return;
              }

              if (productVariant.attributes_count > 0 ) {
                this.openIsDisplayAttributes(productVariant);

              }
            },
            transform: (count: number) => {
              return `<span class="d-block">${count > 0 ? count : '0'}</span>`;
            }
          },
          {
            text: 'Stock',
            value: 'stock',
            transform: (stock: string, productVariant: ProductVariant) => {
              return `<div class="text-right">
                      <span class="d-block white-space-no-wrap">OH: ${productVariant.stock_on_hand} </span>
                      <span class="d-block white-space-no-wrap">AV:  ${stock}</span>
                      <span class="d-block white-space-no-wrap">PO: ${productVariant.stock_purchase_order} </span>
                      <span class="d-block white-space-no-wrap">MQ: ${productVariant.minimum_stock_quantity} </span>
                      </div>
                      `;
            },
          },
          {
            text: 'Is Rerun',
            value: 'is_rerun',
            transform: (is_rerun: boolean) => is_rerun ? 'Yes' : 'No',
          },
          {
            text: 'Is Dummy UPC',
            value: 'is_dummy_upc',
            transform: (is_dummy_upc: boolean) => is_dummy_upc ? 'Yes' : 'No',
          },
          {
            text: 'Created / Block / Return',
            value: 'created_at',
            sortable: {
              key: 'created_at',
              order: 'DESC'
            },
            transform: (date: string, product: ProductVariant) => {
              return `<div>
                      <span class="d-block"><b>Created:</b> ${formatDate(date, 'dd-LL-yyyy')}</span>
                      <span class="d-block"><b>Blocked:</b> ${product.block_date && product.block_date !== '0000-00-00 00:00:00' ? formatDate(product.block_date as string, 'dd-LL-yyyy') : 'Not set'}</span>
                      <span class="d-block"><b>Final Return:</b> ${product.final_return_date && product.final_return_date !== '0000-00-00 00:00:00' ? formatDate(product.final_return_date as string, 'dd-LL-yyyy') : 'Not set'}</span>
                      </div>
                      `;
            },
          },
        ],
        actions: [
          {
            type: 'add',
            label: 'add',
            icon: 'collections',
            action: (vm: any, variant: ProductVariant) => {
              this.variant = variant;
              this.isUploadingImages = true;
            },
            tooltip: 'Upload foto\'s',
          },
          {
            type: 'family',
            label: 'family',
            icon: 'people',
            action: (vm: any, variant: ProductVariant) => {
              return this.openCloneVariantDialog(variant);
            },
            tooltip: 'Clone'
          },
          {
            type: 'view',
            label: 'view',
            icon: 'remove_red_eye',
            action: '/varianten/{id}',
            tooltip: 'Bekijk variant'
          }
        ],
        filters: [],
      }
  }

  protected get items(): ActionItem[] {
    return [
      {
        label: 'Bulk update',
        icon: 'edit',
        action: () => {
          this.isUpdatingBulk = true;
        },
      },
      {
        label: 'Retour leverancier',
        icon: 'reply',
        action: () => {
          this.$store.dispatch("openDialog", this.dialogReturingProducts);
        },
      },
    ]
  }

  protected get dialogReturingProducts(): Options {
    return {
      title: this.$t("dialogOptions.confirmation").toString(),
      text:
        "Weet je zeker dat je de geselecteerde producten wilt retourneren naar de leverancier?",
      type: "warning",
      buttons: {
        confirm: {
          text: "Ok",
          action: () => {
            this.returnProducts();
          }
        },
        cancel: {
          text: this.$t("dialogOptions.button.cancel").toString(),
          action: () => {}
        }
      }
    };
  }

  protected get dialogReturingProductsSuccess(): Options {
    return {
      title: this.$t("dialogOptions.confirmation").toString(),
      text: "Er is een order aangemaakt met de geselecteerde producten.",
      type: "success",
      buttons: {
        confirm: {
          text: "Ga naar order",
          action: () => {
            if (this.selectedOrder) {
              this.$router.push(
                `/webshop/order/${this.selectedOrder.order}/sets`
              );
            }
          }
        },
        cancel: {
          text: this.$t("dialogOptions.button.close").toString(),
          action: () => {
            this.selectedProductVariants = [];
            this.productVariantDatatable
              ? this.productVariantDatatable.clearSelectBoxes()
              : null;
          }
        }
      }
    };
  }
  //#endregion


  //#region @Watchers
  /**
  * ie.
  * @Watch('company')
  * protected companyChanged() {
  *  console.log('Company Changed') ;
  * }
  */
  //#endregion
}

interface exportPayload  {
  product_variants?: string[];
}
